import React from "react";
import { useLang } from "@/context/LangContext";
import RailwayDivider from "@/components/RailwayDivider";
import SEO from "@/components/SEO";
import { Target, Scale, CheckCircle2, Compass, Download } from "lucide-react";

const DOSSIER_PDF_URL = "https://customer-assets-4nw71qhi.emergentagent.net/job_tappe-unesco/artifacts/607yaczt_Pres.%20Video%20%20Cammino.pdf";

const Progetto = () => {
  const { t, lang } = useLang();
  const timeline = [
    { year: "1868", it: "Apertura ferrovia Siracusa–Ragusa–Vizzini", en: "Opening of Syracuse–Ragusa–Vizzini railway" },
    { year: "1956", it: "Ultimo treno sulla linea", en: "Last train on the line" },
    { year: "2024", it: "Studio di fattibilità sul tracciato ciclo-pedonale", en: "Feasibility study on the cycle-pedestrian route" },
    { year: "2025", it: "Prima tappa pilota Modica–Scicli", en: "First pilot stage Modica–Scicli" },
    { year: "2026", it: "Apertura ufficiale delle prime tappe (fase pilota)", en: "Official opening of first stages (pilot phase)" },
    { year: "2027", it: "Estensione ai sentieri secondari e ai centri collegati", en: "Extension to secondary trails and connected centres" },
  ];

  return (
    <div data-testid="progetto-page">
      <SEO
        titleIt="Il Progetto — Storia, obiettivi e timeline del Cammino Ibleo"
        titleEn="The Project — History, objectives and timeline of the Cammino Ibleo"
        descriptionIt="Dalla ferrovia Siracusa–Vizzini–Ragusa (1868) al Cammino ciclo-pedonale del 2026: obiettivi di tutela, riconversione delle infrastrutture dismesse e valorizzazione UNESCO della Sicilia Orientale."
        descriptionEn="From the Syracuse–Vizzini–Ragusa railway (1868) to the 2026 cycle-pedestrian trail: conservation goals, reconversion of disused infrastructure and UNESCO valorisation of Eastern Sicily."
      />
      <section className="pt-40 pb-16 bg-pietra-light border-b border-carrubo/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="eyebrow mb-6">Sicilia Orientale</div>
          <h1 className="font-serif text-5xl md:text-7xl text-grafite mb-4">{t("progetto_title")}</h1>
          <p className="text-lg text-grafite-soft max-w-3xl">{t("progetto_subtitle")}</p>
        </div>
      </section>

      <section className="py-20 bg-pietra-light">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 space-y-16">
          {[
            { icon: Target, title: t("progetto_vision"), body: t("progetto_vision_body"), key: "vision" },
            { icon: Compass, title: t("progetto_marchio_title"), body: t("progetto_marchio_body"), key: "marchio" },
            { icon: Scale, title: t("progetto_align"), body: t("progetto_align_body"), key: "align" },
          ].map((s, i) => s.key === "marchio" ? (
            <div
              key={i}
              data-testid="progetto-marchio-block"
              className="grid md:grid-cols-[minmax(240px,340px)_1fr] gap-10 md:gap-14 items-center bg-carrubo/5 border border-carrubo/15 rounded-sm p-8 md:p-12"
            >
              <div className="flex justify-center md:justify-start">
                <img
                  src="/images/ibleo-cammino-unesco-logo.png"
                  alt="Marchio Cammino Ibleo UNESCO"
                  data-testid="progetto-marchio-logo"
                  className="w-full max-w-[340px] h-auto drop-shadow-[0_6px_20px_rgba(0,0,0,0.15)]"
                  draggable="false"
                />
              </div>
              <div>
                <h2 className="font-serif text-3xl md:text-4xl text-grafite mb-4">{s.title}</h2>
                <RailwayDivider className="mb-6 opacity-40" />
                <p className="text-lg text-grafite-soft leading-relaxed">{s.body}</p>
              </div>
            </div>
          ) : (
            <div key={i} className="grid md:grid-cols-[80px_1fr] gap-8 items-start">
              <div className="w-14 h-14 bg-carrubo text-pietra-light rounded-sm flex items-center justify-center">
                <s.icon size={22} strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="font-serif text-3xl md:text-4xl text-grafite mb-4">{s.title}</h2>
                <RailwayDivider className="mb-6 opacity-40" />
                <p className="text-lg text-grafite-soft leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="progetto-objectives" className="py-20 bg-pietra">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="eyebrow text-miele mb-4">{lang === "it" ? "Piano d'azione" : "Action plan"}</div>
          <h2 className="font-serif text-4xl md:text-5xl text-grafite mb-10">{t("progetto_objectives_title")}</h2>
          <RailwayDivider className="mb-10 opacity-40" />
          <ul className="grid md:grid-cols-2 gap-5">
            {t("progetto_objectives").map((obj, i) => (
              <li key={i} className="flex gap-4 bg-pietra-light p-5 rounded-sm border border-carrubo/10">
                <CheckCircle2 size={22} className="text-miele shrink-0 mt-1" strokeWidth={1.5} />
                <span className="text-grafite leading-relaxed">{obj}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section data-testid="progetto-dossier" className="py-24 bg-carrubo text-pietra-light">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid md:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <div className="eyebrow text-miele mb-4">{lang === "it" ? "Documento ufficiale" : "Official document"}</div>
            <h2 className="font-serif text-4xl md:text-5xl mb-4">{t("progetto_dossier_title")}</h2>
            <p className="text-pietra-light/80 text-lg leading-relaxed max-w-2xl">{t("progetto_dossier_body")}</p>
          </div>
          <a
            data-testid="dossier-download-link"
            href={DOSSIER_PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            download="Cammino-Ibleo-UNESCO-Dossier.pdf"
            className="inline-flex items-center gap-3 bg-miele hover:bg-miele-dark text-grafite font-semibold px-7 py-4 rounded-sm transition-colors small-caps text-sm tracking-widest whitespace-nowrap"
          >
            <Download size={18} strokeWidth={2} />
            {t("progetto_dossier_cta")}
          </a>
        </div>
      </section>

      <section className="py-20 bg-grafite text-pietra-light">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="eyebrow text-miele mb-4">Timeline</div>
          <h2 className="font-serif text-4xl md:text-5xl mb-12">
            {lang === "it" ? "Un secolo e mezzo di binari, un futuro di cammino" : "A century and a half of tracks, a future of walking"}
          </h2>
          <div className="relative">
            <div className="absolute left-0 md:left-1/2 top-0 bottom-0 opacity-60" style={{ background: "repeating-linear-gradient(to bottom, #C99B4A 0, #C99B4A 12px, transparent 12px, transparent 20px)", width: 2 }} />
            {timeline.map((tl, i) => (
              <div key={i} className={`relative flex items-center gap-8 mb-12 ${i % 2 === 0 ? "md:justify-start" : "md:justify-end md:flex-row-reverse"}`}>
                <div className={`w-full md:w-5/12 pl-10 md:pl-0 ${i % 2 === 0 ? "md:text-right md:pr-16" : "md:text-left md:pl-16"}`}>
                  <div className="font-serif text-5xl text-miele mb-2">{tl.year}</div>
                  <div className="text-lg text-pietra-light/85">{lang === "it" ? tl.it : tl.en}</div>
                </div>
                <div className="absolute left-0 md:left-1/2 -translate-x-1/2 w-4 h-4 bg-miele rounded-full border-4 border-grafite" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Progetto;
